import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import { getAuditUniverse, listAudits } from "../../api/audit.api";
import Button from "../../ui/Button";
import PageState from "../../ui/PageState";
import StatusBadge from "../../ui/StatusBadge";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";
import {
  pageHeaderActionsStyle,
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  surfaceCardBodyStyle,
  surfaceCardHeaderStyle,
  surfaceCardStyle,
  toolbarStyle,
} from "../../ui/pagePatterns";
import PinButton from "../../components/pins/PinButton";
import useRegisterRecentView from "../../features/recentViews/useRegisterRecentView";

const shellStyle = {
  ...pageShellStyle,
  gap: spacing.xl,
};

const sectionCardStyle = {
  ...surfaceCardStyle,
};

const sectionBodyStyle = {
  ...surfaceCardBodyStyle,
  display: "grid",
  gap: spacing.lg,
};

const descriptionStyle = {
  margin: 0,
  color: colors.textSecondary,
  fontSize: typography.size.sm,
  lineHeight: 1.6,
};

const infoGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: spacing.md,
};

const linkedAuditCardStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surface,
  padding: spacing.xl,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: spacing.md,
  flexWrap: "wrap",
  boxShadow: shadows.xs,
};

const linkedAuditMetaStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
};

export default function AuditUniverseDetail({ auditUniverseId = null, inline = false }) {
  const { id: routeId } = useParams();
  const id = auditUniverseId ?? routeId;
  const navigate = useNavigate();

  const universeQuery = useQuery({
    queryKey: ["audit-universe", id],
    queryFn: () => getAuditUniverse(id).then((res) => res.data),
  });

  const auditsQuery = useQuery({
    queryKey: ["audits", { universe_item_id: id }],
    queryFn: () => listAudits({ universe_item_id: id }).then((res) => res.data),
    enabled: !!id,
  });

  const item = universeQuery.data;
  useRegisterRecentView({
    itemType: "audit_universe",
    itemId: item?.id || id,
    title: item?.name,
    secondaryText: item?.entity_type || item?.business_area || null,
    enabled: Boolean(item?.id && item?.name),
  });

  if (universeQuery.isLoading) {
    return (
      <PageState
        state="loading"
        align="left"
        title="Loading auditable entity"
        description="Glyder is preparing the selected entity, planning metadata, and linked audit context."
      />
    );
  }

  if (universeQuery.isError) {
    return (
      <PageState
        state="error"
        align="left"
        title="Auditable entity is unavailable"
        description={`${universeQuery.error?.message || "Glyder could not load this auditable entity."} Refresh the workspace to try again.`}
      />
    );
  }

  return (
    <div style={shellStyle}>
      {!inline && <Breadcrumbs items={[{ label: "Audit Planning", href: "/audit/planning?view=entities" }, { label: item?.name }]} />}

      <div style={pageHeaderStyle}>
        <div style={pageHeaderTitleBlockStyle}>
          <h1 style={pageTitleStyle}>{item?.name}</h1>
          <p style={pageSubtitleStyle}>
            Review planning metadata, ownership, and linked audits for this auditable entity.
          </p>
          <div style={{ ...toolbarStyle, gap: spacing.xs }}>
            <StatusBadge value={item?.audit_priority || item?.audit_priority_override || "Unrated"} />
            <StatusBadge value={item?.residual_risk_rating || item?.inherent_risk_rating || "Unrated"} />
          </div>
        </div>
        <div style={pageHeaderActionsStyle}>
          {!inline && (
            <PinButton
              itemType="audit_universe"
              itemId={id}
              summary={{
                title: item?.name || "Auditable entity",
                secondaryText: [item?.business_area, item?.entity_type].filter(Boolean).join(" | "),
                status: item?.audit_priority || item?.residual_risk_rating || item?.inherent_risk_rating || null,
                ownerDisplay: item?.primary_owner_id || null,
                updatedAt: item?.updated_at || null,
              }}
            />
          )}
          <Button onClick={() => navigate("/audit/planning?view=entities")} variant="primary">
            Open Audit Planning
          </Button>
        </div>
      </div>

      <div style={sectionCardStyle}>
        <div style={surfaceCardHeaderStyle}>
          <div style={{ fontWeight: typography.weight.bold, color: colors.text }}>Overview</div>
        </div>
        <div style={sectionBodyStyle}>
          <p style={descriptionStyle}>{item?.description || "No description provided."}</p>
          <div style={infoGridStyle}>
            <Info label="Entity Type" value={item?.entity_type} />
            <Info label="Business Area" value={item?.business_area} />
            <Info label="Risk Rating" value={item?.residual_risk_rating || item?.inherent_risk_rating} />
            <Info label="Frequency" value={item?.audit_frequency} />
            <Info label="Next Planned Audit" value={item?.next_planned_audit_date} />
            <Info label="Priority" value={item?.audit_priority || item?.audit_priority_override} />
          </div>
        </div>
      </div>

      <div style={sectionCardStyle}>
        <div style={surfaceCardHeaderStyle}>
          <div style={{ fontWeight: typography.weight.bold, color: colors.text }}>Ownership and RACI</div>
        </div>
        <div style={sectionBodyStyle}>
          <div style={infoGridStyle}>
            <Info label="Primary Owner" value={item?.primary_owner_id} />
            <Info label="Secondary Owner" value={item?.secondary_owner_id} />
            <Info label="Audit Sponsor" value={item?.audit_sponsor_id} />
            <Info label="Current Lead" value={item?.current_audit_lead_id} />
          </div>
        </div>
      </div>

      <div style={sectionCardStyle}>
        <div style={surfaceCardHeaderStyle}>
          <div style={{ fontWeight: typography.weight.bold, color: colors.text }}>Linked Audits</div>
        </div>
        <div style={sectionBodyStyle}>
          {auditsQuery.isLoading ? (
            <PageState
              state="loading"
              align="left"
              compact
              title="Loading audits"
              description="Preparing audit records linked to this auditable entity."
            />
          ) : !auditsQuery.data?.items?.length ? (
            <PageState
              state="empty"
              align="left"
              title="No audits linked yet"
              description="Add this auditable entity to an approved audit plan, then create an audit from that plan item."
              actions={
                <>
                  <Button onClick={() => navigate("/audit/planning?view=entities")} variant="primary">
                    Open Audit Planning
                  </Button>
                  <Button onClick={() => navigate("/controls")} variant="secondary">
                    Review controls
                  </Button>
                </>
              }
            />
          ) : (
            <div style={{ display: "grid", gap: spacing.sm }}>
              {auditsQuery.data.items.map((audit) => (
                <div
                  key={audit.id}
                  style={linkedAuditCardStyle}
                  onClick={() => navigate(`/audit/audits/${audit.id}`)}
                >
                  <div style={{ display: "grid", gap: spacing.xs / 2 }}>
                    <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>{audit.title}</div>
                    <div style={linkedAuditMetaStyle}>
                      {audit.status} • Step: {audit.current_step}
                    </div>
                  </div>
                  <div style={linkedAuditMetaStyle}>Lead: {audit.audit_lead_id || "Unassigned"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div
      style={{
        background: colors.surfaceSubtle,
        border: `1px solid ${colors.border}`,
        padding: `${spacing.md + spacing.xs}px ${spacing.xl}px`,
        borderRadius: radius.md,
        display: "grid",
        gap: spacing.xs / 2,
      }}
    >
      <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{label}</div>
      <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>{value || "-"}</div>
    </div>
  );
}
